/**
 * Proteção das rotas que exigem login.
 *
 * Papel: este arquivo exporta o `LayoutProtegido`, que é o componente montado
 * na rota `/` do `router.tsx`, e o `DocumentoProtegido`, que monta o grupo das
 * páginas de documento. Os dois levam quem chegar sem sessão aberta ao Auth0 e
 * o devolvem, depois de entrar, exatamente para a página que tentou abrir; o
 * primeiro desenha o `Layout` em volta da página, e o segundo desenha só a
 * página.
 *
 * Como usar: nenhuma rota precisa repetir esta proteção. Como todo objeto
 * declarado em `children` é filho da rota `/` ou do grupo de documentos, uma
 * tela nova já passa a exigir login por conta própria, e envolvê-la numa
 * segunda camada apenas duplicaria o redirecionamento.
 *
 * Esta é a barreira de conveniência, e não a de segurança: o que ela decide é
 * apenas o que a interface mostra. Quem de fato impede o acesso ao dado é a
 * Function, que confere o JWT antes de tocar no banco, e por isso um front
 * adulterado não consegue passar por ela.
 */
import { withAuthenticationRequired } from '@auth0/auth0-react';
import { Outlet } from 'react-router';

import { Layout } from './layout';

/**
 * Tela mostrada enquanto o Auth0 resolve a sessão ou redireciona.
 *
 * O aviso é deliberadamente estático. Na maior parte das vezes ele dura poucos
 * milissegundos, e um indicador animado que aparece e some dentro do mesmo
 * quadro incomoda mais do que a ausência dele, de modo que acrescentar um
 * spinner aqui piora a percepção de velocidade em vez de melhorar.
 *
 * @returns Aviso centralizado na tela, em tom discreto.
 */
function TelaDeEspera() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background">
      <p className="text-sm text-muted-foreground">Verificando sessão…</p>
    </div>
  );
}

/**
 * Layout da aplicação, acessível apenas com sessão ativa.
 *
 * É este o componente que o `router.tsx` monta na rota `/`, e todas as demais
 * rotas são filhas dele, de modo que a exigência de sessão passa a valer para a
 * árvore inteira sem que cada rota precise repeti-la.
 *
 * O `returnTo` é declarado como função, e não como string, porque ele precisa
 * ser calculado no instante do redirecionamento: é só nesse momento que o
 * `window.location` guarda o endereço que a pessoa tentou abrir. Trocá-lo por
 * um valor fixo faria todo mundo voltar do login para a mesma tela.
 */
export const LayoutProtegido = withAuthenticationRequired(Layout, {
  onRedirecting: () => <TelaDeEspera />,
  returnTo: () => `${window.location.pathname}${window.location.search}`,
});

/**
 * O grupo das páginas de documento — recibo e ordem de pagamento —, acessível
 * apenas com sessão ativa e sem o `Layout`: a página é só o documento, que vai
 * para o papel como está.
 *
 * A aba nova que o botão abre já chega com sessão, porque o token fica guardado
 * no navegador; sem ele, a pessoa passa pelo Auth0 e volta ao documento.
 */
export const DocumentoProtegido = withAuthenticationRequired(Outlet, {
  onRedirecting: () => <TelaDeEspera />,
  returnTo: () => `${window.location.pathname}${window.location.search}`,
});
